/**
 * UnderstaffingSummary — Year-by-year understaffing table with expandable acuity bars.
 * Adapted from eliotnest_core UnderstaffingSummary.tsx (prosecution baselines removed).
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { CostReport } from '../../types';

type AcuityModel = 'anticipated' | 'hybrid';

interface Props {
  facility: any;
  facilityYears: any[];
  stateContext?: any;
  turnoverBenchmarks?: any;
  costReports?: CostReport[];
  model?: AcuityModel;
  onModelChange?: (model: AcuityModel) => void;
}

// ── Helpers ──

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  if (val === 0) return '$0';
  const absVal = Math.abs(val);
  if (absVal >= 1000000) return `$${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `$${Math.round(absVal / 1000).toLocaleString()}K`;
  return `$${Math.round(absVal).toLocaleString()}`;
};

const fmtAcuityGap = (pctStaffed: number | null | undefined) => {
  if (pctStaffed == null) return <span style={{ color: '#bdbdbd' }}>-</span>;
  const gap = pctStaffed - 100;
  const color = gap < 0 ? '#d32f2f' : '#2e7d32';
  const sign = gap >= 0 ? '+' : '';
  return <span style={{ fontWeight: 700, color }}>{sign}{gap.toFixed(1)}%</span>;
};

const fmtPctDays = (val: number | null | undefined) => {
  if (val == null) return <span style={{ color: '#bdbdbd' }}>-</span>;
  const color = val > 50 ? '#d32f2f' : val > 25 ? '#ed6c02' : undefined;
  return <span style={{ fontWeight: 600, color }}>{val.toFixed(1)}%</span>;
};

const fmtAcuityPct = (val: number | null | undefined) => {
  if (val == null) return '-';
  const diff = val - 100;
  const color = diff < 0 ? '#d32f2f' : '#2e7d32';
  const sign = diff >= 0 ? '+' : '';
  return <span style={{ fontWeight: 700, color }}>{sign}{diff.toFixed(1)}%</span>;
};

const MAX_PCT = 200;
const COLORS: Record<string, string> = {
  anticipated: 'rgba(21, 101, 192, 0.85)',
  hybrid: 'rgba(123, 31, 162, 0.85)',
};

// ── Data computation ──

function computeAcuity(years: any[], model: AcuityModel = 'anticipated') {
  const p = model === 'hybrid' ? 'hybrid' : 'ant';
  let pbjRn = 0, mdsRn = 0, pbjLpn = 0, mdsLpn = 0, pbjCna = 0, mdsAide = 0;
  for (const fy of years) {
    pbjRn += fy[`${p}_pbj_rn_hrs`] || 0;
    mdsRn += fy[`${p}_expected_rn_hrs_hours`] || 0;
    pbjLpn += fy[`${p}_pbj_lpn_hrs`] || 0;
    mdsLpn += fy[`${p}_expected_lpn_hrs_hours`] || 0;
    pbjCna += fy[`${p}_pbj_cna_hrs`] || 0;
    mdsAide += fy[`${p}_expected_cna_hrs_hours`] || 0;
  }
  const totalPbj = pbjRn + pbjLpn + pbjCna;
  const totalMds = mdsRn + mdsLpn + mdsAide;
  return {
    total: totalMds > 0 ? Math.round((totalPbj / totalMds) * 1000) / 10 : null,
    rn: mdsRn > 0 ? Math.round((pbjRn / mdsRn) * 1000) / 10 : null,
    lpn: mdsLpn > 0 ? Math.round((pbjLpn / mdsLpn) * 1000) / 10 : null,
    aide: mdsAide > 0 ? Math.round((pbjCna / mdsAide) * 1000) / 10 : null,
  };
}

interface ModelYearRow {
  year: number;
  acuityGap: number | null;
  pctDaysFailing: number | null;
  costNoCredit: number;
  costWithCredit: number;
  acuityRn: number | null;
  acuityLpn: number | null;
  acuityAide: number | null;
}

function buildModelYearRows(facilityYears: any[], model: AcuityModel): ModelYearRow[] {
  const p = model === 'hybrid' ? 'hybrid' : 'ant';
  const pctDaysKey = model === 'hybrid' ? 'hybrid_pct_days_any_below' : 'pct_ant_days_any_below';
  const costKey = model === 'hybrid' ? 'hybrid_understaffing_cost' : 'mds_understaffing_cost';
  const costNoCreditKey = model === 'hybrid' ? 'hybrid_understaffing_cost_no_credit' : 'mds_understaffing_cost_no_credit';

  return facilityYears
    .map((fy: any) => {
      const costWithCredit = Math.max(0, fy[costKey] ?? 0);
      const costNoCredit = Math.max(0, fy[costNoCreditKey] ?? 0);

      const pbjTotal = (fy[`${p}_pbj_rn_hrs`] || 0) + (fy[`${p}_pbj_lpn_hrs`] || 0) + (fy[`${p}_pbj_cna_hrs`] || 0);
      const mdsTotal = (fy[`${p}_expected_rn_hrs_hours`] || 0) + (fy[`${p}_expected_lpn_hrs_hours`] || 0) + (fy[`${p}_expected_cna_hrs_hours`] || 0);
      const acuityGap = mdsTotal > 0 ? Math.round((pbjTotal / mdsTotal) * 1000) / 10 : null;

      const acuityRn = (fy[`${p}_expected_rn_hrs_hours`] || 0) > 0 ? Math.round(((fy[`${p}_pbj_rn_hrs`] || 0) / fy[`${p}_expected_rn_hrs_hours`]) * 1000) / 10 : null;
      const acuityLpn = (fy[`${p}_expected_lpn_hrs_hours`] || 0) > 0 ? Math.round(((fy[`${p}_pbj_lpn_hrs`] || 0) / fy[`${p}_expected_lpn_hrs_hours`]) * 1000) / 10 : null;
      const acuityAide = (fy[`${p}_expected_cna_hrs_hours`] || 0) > 0 ? Math.round(((fy[`${p}_pbj_cna_hrs`] || 0) / fy[`${p}_expected_cna_hrs_hours`]) * 1000) / 10 : null;

      return {
        year: fy.year,
        acuityGap,
        pctDaysFailing: fy[pctDaysKey] ?? null,
        costNoCredit,
        costWithCredit,
        acuityRn,
        acuityLpn,
        acuityAide,
      };
    })
    .filter(r => r.year != null)
    .sort((a, b) => a.year - b.year);
}

// ── Acuity bar chart (expandable row) ──

interface AcuityBarRow { label: string; facilityPct: number | null; statePct: number | null }

const AcuityChart: React.FC<{ rows: AcuityBarRow[] }> = ({ rows }) => (
  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 10, backgroundColor: '#66bb6a', opacity: 0.8, borderRadius: 0.5 }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Facility</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 10, backgroundColor: '#e3f2fd', border: '1px dashed #90caf9', borderRadius: 0.5 }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>State Avg</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderLeft: '2.5px solid #212121', opacity: 0.7 }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>100%</Typography>
        </Box>
      </Box>
      {rows.map(row => {
        const fac = row.facilityPct ?? 0;
        const st = row.statePct ?? 0;
        const facWidth = Math.min(fac, MAX_PCT) / MAX_PCT * 100;
        const stWidth = Math.min(st, MAX_PCT) / MAX_PCT * 100;
        const markerLeft = (100 / MAX_PCT) * 100;
        const isBelow = fac < 100;
        return (
          <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, width: 40, color: 'text.secondary', flexShrink: 0 }}>{row.label}</Typography>
            <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
              <Box sx={{ position: 'absolute', left: 0, top: 3, bottom: 3, width: `${stWidth}%`, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px dashed #90caf9' }} />
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${facWidth}%`, backgroundColor: isBelow ? '#ef5350' : '#66bb6a', opacity: 0.8, borderRadius: 1, transition: 'width 0.3s ease' }} />
              <Tooltip title="100% = staffed exactly to acuity benchmark" arrow placement="top">
                <Box sx={{ position: 'absolute', left: `${markerLeft}%`, top: -3, bottom: -3, width: 2.5, backgroundColor: '#212121', opacity: 0.7, zIndex: 2, cursor: 'help', ml: '-1px', px: '3px', mx: '-3px' }} />
              </Tooltip>
            </Box>
          </Box>
        );
      })}
    </Box>
    <Box sx={{ flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minWidth: 260 }}>
      <Box sx={{ display: 'flex', backgroundColor: '#f5f5f5', borderBottom: '2px solid', borderColor: 'divider' }}>
        <Box sx={{ width: 60, px: 1.5, py: 0.5 }}>&nbsp;</Box>
        <Box sx={{ width: 90, px: 1.5, py: 0.5, textAlign: 'right', borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Facility</Typography>
        </Box>
        <Box sx={{ width: 90, px: 1.5, py: 0.5, textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>State</Typography>
        </Box>
      </Box>
      {rows.map((row, idx) => (
        <Box key={row.label} sx={{ display: 'flex', borderBottom: idx === rows.length - 1 ? 'none' : '1px solid', borderColor: 'divider' }}>
          <Box sx={{ width: 60, px: 1.5, py: 0.75, borderRight: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>{row.label}</Typography>
          </Box>
          <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right', borderRight: '1px solid', borderColor: 'divider' }}>
            {fmtAcuityPct(row.facilityPct)}
          </Box>
          <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1976d2' }}>
              {row.statePct != null ? `${(row.statePct - 100) >= 0 ? '+' : ''}${(row.statePct - 100).toFixed(1)}%` : '-'}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Main component ──

export default function UnderstaffingSummary({ facility, facilityYears, stateContext, turnoverBenchmarks, costReports, model: externalModel, onModelChange }: Props) {
  const [internalModel, setInternalModel] = useState<AcuityModel>('anticipated');
  const model = externalModel ?? internalModel;
  const setModel = onModelChange ?? setInternalModel;
  const [expandedYear, setExpandedYear] = useState<number | 'total' | null>(null);

  const anticipatedRows = useMemo(() => buildModelYearRows(facilityYears, 'anticipated'), [facilityYears]);
  const hybridRows = useMemo(() => buildModelYearRows(facilityYears, 'hybrid'), [facilityYears]);
  const rows = model === 'anticipated' ? anticipatedRows : hybridRows;

  const totalCostNoCredit = rows.reduce((s, r) => s + r.costNoCredit, 0);
  const totalCostWithCredit = rows.reduce((s, r) => s + r.costWithCredit, 0);

  const overallAcuity = useMemo(() => computeAcuity(facilityYears, model), [model, facilityYears]);
  const overallDaysVals = rows.map(r => r.pctDaysFailing).filter((v): v is number => v != null);
  const overallPctDaysFailing = overallDaysVals.length > 0 ? overallDaysVals.reduce((a, b) => a + b, 0) / overallDaysVals.length : null;

  const modelLabel = model === 'anticipated' ? 'Anticipated' : 'Hybrid';

  const stateAcuity = useMemo(() => {
    if (!stateContext) return null;
    if (model === 'hybrid') {
      return { total: stateContext.hybrid_acuity_total, rn: stateContext.hybrid_acuity_rn, lpn: stateContext.hybrid_acuity_lpn, aide: stateContext.hybrid_acuity_cna };
    }
    return { total: stateContext.avg_acuity_total, rn: stateContext.avg_acuity_rn, lpn: stateContext.avg_acuity_lpn, aide: stateContext.avg_acuity_cna };
  }, [model, stateContext]);

  const buildBarRows = (acuity: { rn: number | null; lpn: number | null; aide: number | null }): AcuityBarRow[] => [
    { label: 'RN', facilityPct: acuity.rn, statePct: stateAcuity?.rn ?? null },
    { label: 'LPN', facilityPct: acuity.lpn, statePct: stateAcuity?.lpn ?? null },
    { label: 'CNA', facilityPct: acuity.aide, statePct: stateAcuity?.aide ?? null },
  ];

  if (facilityYears.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Understaffing</Typography>
        <Typography color="text.secondary">No understaffing data available for the selected cost report years.</Typography>
      </Box>
    );
  }

  const accentColor = COLORS[model];
  const yearRange = `${rows[0]?.year ?? ''}–${rows[rows.length - 1]?.year ?? ''}`;
  const handleRowClick = (year: number | 'total') => setExpandedYear(expandedYear === year ? null : year);

  return (
    <Box sx={{ mb: 4 }}>
      {/* Description + model toggle on one line */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Based on CMS PBJ staffing and MDS acuity data for the selected cost report periods
        </Typography>
        <ToggleButtonGroup
          value={model}
          exclusive
          onChange={(_, v) => { if (v) { setModel(v); setExpandedYear(null); } }}
          sx={{
            '& .MuiToggleButton-root': {
              py: 0.75, px: 2.5, fontSize: '0.85rem', fontWeight: 600, textTransform: 'none',
              '&.Mui-selected': { backgroundColor: accentColor, color: 'white', '&:hover': { backgroundColor: accentColor } },
            },
          }}
        >
          <ToggleButton value="anticipated">Anticipated</ToggleButton>
          <ToggleButton value="hybrid">Hybrid</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: `Total Acuity Gap\n(${modelLabel}) ${yearRange}`, value: fmtAcuityGap(overallAcuity.total), tooltip: 'Negative is understaffed, positive is overstaffed' },
          { label: `% Days Failing to Staff\nto Acuity (${modelLabel})`, value: fmtPctDays(overallPctDaysFailing), tooltip: 'Percent of days where at least one nursing category (RN, LPN, or CNA) was staffed below its acuity-expected hours' },
          { label: `Projected Understaffing\n(${modelLabel}, No Credit)`, value: <span style={{ fontWeight: 700 }}>{formatMoney(totalCostNoCredit)}</span>, tooltip: 'No credit for overstaffing one of the three nursing care categories' },
          { label: `Projected Understaffing\n(${modelLabel}, w/ Credit)`, value: <span style={{ fontWeight: 700 }}>{formatMoney(totalCostWithCredit)}</span>, tooltip: 'Credit given for overstaffing one of the three nursing care categories' },
        ].map((card, i) => (
          <Box key={i} sx={{ flex: 1, minWidth: 180, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28, whiteSpace: 'pre-line' }}>
              {card.label}
              {card.tooltip && (
                <Tooltip title={card.tooltip} arrow>
                  <HelpOutlineIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: 'middle', cursor: 'help', color: 'text.disabled' }} />
                </Tooltip>
              )}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{card.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Section title */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Year-by-Year Breakdown</Typography>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, width: 50 }}></th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700 }}>Year</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Negative is understaffed, positive is overstaffed" arrow><span>Total Acuity Gap</span></Tooltip>
              </th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Percent of days where at least one nursing category (RN, LPN, or CNA) was staffed below its acuity-expected hours" arrow><span>% Days Failing to<br />Staff to Acuity</span></Tooltip>
              </th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="No credit for overstaffing one of the three nursing care categories" arrow><span>Projected Understaffing<br />Without Credit</span></Tooltip>
              </th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Credit given for overstaffing one of the three nursing care categories" arrow><span>Projected Understaffing<br />With Credit</span></Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isExpanded = expandedYear === row.year;
              return (
                <React.Fragment key={row.year}>
                  <tr onClick={() => handleRowClick(row.year)} style={{ borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: isExpanded ? '#fafbfc' : undefined }}>
                    <td style={{ padding: '8px 4px 8px 12px', width: 30 }}>
                      <IconButton size="small" sx={{ p: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{row.year}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtAcuityGap(row.acuityGap)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtPctDays(row.pctDaysFailing)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: accentColor }}>{formatMoney(row.costNoCredit)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: accentColor }}>{formatMoney(row.costWithCredit)}</td>
                  </tr>
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderBottom: '2px solid', borderColor: 'divider', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)' }}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                            {row.year} Staffing as % of Acuity by Staffing Group
                          </Typography>
                          <AcuityChart rows={buildBarRows({ rn: row.acuityRn, lpn: row.acuityLpn, aide: row.acuityAide })} />
                        </Box>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr onClick={() => handleRowClick('total')} style={{ borderTop: '2px solid #e0e0e0', cursor: 'pointer', backgroundColor: expandedYear === 'total' ? '#fafbfc' : undefined }}>
              <td style={{ padding: '8px 4px 8px 12px', width: 30 }}>
                <IconButton size="small" sx={{ p: 0, transform: expandedYear === 'total' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </td>
              <td style={{ padding: '8px 12px', fontWeight: 700 }}>Total</td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtAcuityGap(overallAcuity.total)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtPctDays(overallPctDaysFailing)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: accentColor }}>{formatMoney(totalCostNoCredit)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: accentColor }}>{formatMoney(totalCostWithCredit)}</td>
            </tr>
            <tr>
              <td colSpan={6} style={{ padding: 0 }}>
                <Collapse in={expandedYear === 'total'} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderBottom: '2px solid', borderColor: 'divider', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                      All Years Staffing as % of Acuity by Staffing Group
                    </Typography>
                    <AcuityChart rows={buildBarRows(overallAcuity)} />
                  </Box>
                </Collapse>
              </td>
            </tr>
          </tfoot>
        </table>
      </Box>
    </Box>
  );
}
